import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

// Ant Design
import { 
  Form, 
  Card, 
  Select, 
  message, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Input, 
  Space 
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';

import {
    LICENSE_TYPES,
    LICENSE_STATUS,
    getLicense,
    createLicense,
    updateLicense
} from '../../services/licenseService';
// Components
import CustomerSelect from './components/CustomerSelect';
import LicenseKeyField from './components/LicenseKeyField';
import LicenseTypeField from './components/LicenseTypeField';
import ValidityPeriodSection from './components/ValidityPeriodSection';
import UsageRestrictionsSection from './components/UsageRestrictionsSection';



const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const LicenseForm = ({ isEdit = false }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchLicense = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      // Get the license data
      const data = await getLicense(id);
      
      if (!data) {
        message.error('Failed to load license data');
        navigate('/admin/licenses');
        return;
      }
      
      
      // Prepare form values
      const formValues = {
        ...data,
        expiry_date: data.expiry_date ? moment(data.expiry_date) : null,
        activation_date: data.activation_date ? moment(data.activation_date) : null,
        license_type: data.license_type || LICENSE_TYPES.SUBSCRIPTION,
        // Set customer_id from the first available source
        customer_id: data.customer_id || 
                   (Array.isArray(data.customer_licenses) && data.customer_licenses[0]?.customer_id) ||
                   data.customer_licenses?.customer_id ||
                   data.customer_licenses?.customer?.id,
        // Ensure customer_licenses is always an array of objects with customer_id
        customer_licenses: data.customer_licenses
          ? Array.isArray(data.customer_licenses)
            ? data.customer_licenses
            : [{
                customer_id: data.customer_licenses.customer_id || data.customer_licenses.customer?.id || data.customer_licenses,
                customer: data.customer_licenses.customer,
                is_primary: data.customer_licenses.is_primary || true
              }]
          : data.customer_id
            ? [{
                customer_id: data.customer_id,
                is_primary: true
              }]
            : []
      };
      
      // Set form values
      form.setFieldsValue(formValues);
      // Update local state based on license type
      setLicenseType(data.license_type || LICENSE_TYPES.SUBSCRIPTION);
    } catch (error) {
      console.error('Error fetching license:', error);
      message.error('Failed to load license data');
      navigate('/admin/licenses');
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate]);

  // Log form values when they change
  //const formValues = Form.useWatch([], form);
  // useEffect(() => {
  //   console.log('Form values changed:', formValues);
  // }, [formValues]);

  // Set default values for the form
  const defaultValues = {
    license_type: LICENSE_TYPES.SUBSCRIPTION,
    status: LICENSE_STATUS.ACTIVE,
    grace_period_days: 30,
    max_companies: 1,
    max_employees: 50,
    max_users: 50,
    max_mac_address_change: 5,
  };

  useEffect(() => {
    // Reset form when switching between edit and create mode
    form.resetFields();
    
    if (isEdit && id) {
      fetchLicense();
    } else {
      form.setFieldsValue(defaultValues);
      setLicenseType(LICENSE_TYPES.SUBSCRIPTION);
    }
  }, [id, isEdit, fetchLicense, form]);

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      // Format dates to ISO 8601 with timezone
      const formattedValues = {
        ...values,
        expiry_date: values.expiry_date ? values.expiry_date.toISOString() : null,
        activation_date: values.activation_date ? values.activation_date.toISOString() : null,
        // Ensure license_type is in uppercase
        license_type: values.license_type ? values.license_type.toUpperCase() : LICENSE_TYPES.SUBSCRIPTION,
        max_mac_address_change: values.max_mac_address_change || 5,
        license_key: values.license_key || null,
      };
      
      if (isEdit && id) {
        const response = await updateLicense(id, formattedValues);
        if (response) {
          message.success('License updated successfully');
          // Refresh the form with updated data
          await fetchLicense();
        }
      } else {
        const response = await createLicense(formattedValues);
        if (response) {
          message.success('License created successfully');
          navigate('/admin/licenses');
        }
      }
    } catch (error) {
      console.error('Error saving license:', error);
      message.error(`Failed to ${isEdit ? 'update' : 'create'} license`);
    } finally {
      setSubmitting(false);
    }
  };

  const [licenseType, setLicenseType] = useState(LICENSE_TYPES.SUBSCRIPTION);

  const handleTypeChange = (value) => {
    const typeValue = value ? value.toUpperCase() : LICENSE_TYPES.SUBSCRIPTION;
    setLicenseType(typeValue);
    form.setFieldsValue({ license_type: typeValue });
    // setIsOffline(value === LICENSE_TYPES.PERPETUAL);
  };

  return (
    <div className="license-form">
      <Card 
        title={
          <Space>
            <Button 
              type="text" 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/admin/licenses')}
              style={{ marginRight: 8 }}
            />
            <Title level={5} style={{ margin: 0 }}>
              {isEdit ? 'Edit License' : 'Create New License'}
            </Title>
          </Space>
        }
        loading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={defaultValues}
        >
          <Row gutter={24}>
            <Col xs={24} md={isEdit ? 6 : 6}>
              <CustomerSelect form={form} isEdit={isEdit} />
            </Col>
            <Col xs={24} md={isEdit ? 6 : 6}>
              <LicenseKeyField form={form} isEdit={isEdit} />
            </Col>
            <Col xs={24} md={isEdit ? 6 : 6}>
              <LicenseTypeField 
                form={form} 
                isEdit={isEdit} 
                onTypeChange={handleTypeChange} 
              />
            </Col>
            {isEdit && (
              <Col xs={24} md={isEdit ? 4 : 6}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[{ required: true, message: 'Please select a status' }]}
                >
                  <Select>
                      <Option value={LICENSE_STATUS.ACTIVE}>Active</Option>
                      <Option value={LICENSE_STATUS.DEACTIVATED}>Deactivated</Option>
                      <Option value={LICENSE_STATUS.WARNING}>Warning</Option>
                      <Option value={LICENSE_STATUS.EXPIRED}>Expired</Option>
                      <Option value={LICENSE_STATUS.PENDING}>Pending</Option>
                      <Option value={LICENSE_STATUS.SUSPENDED}>Suspended</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          {licenseType === LICENSE_TYPES.SUBSCRIPTION && (
            <ValidityPeriodSection 
              form={form} 
              isEdit={isEdit} 
              initialValues={form.getFieldsValue(true)} 
              
            />
          )}

          <UsageRestrictionsSection isEdit={isEdit} />

          <Form.Item
            label="Notes"
            name="notes"
            extra="Any additional information about this license"
          >
            <TextArea rows={4} placeholder="Enter any additional notes about this license" />
          </Form.Item>

          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button type="default" onClick={() => navigate(-1)} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />}>
                {isEdit ? 'Update License' : 'Create License'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default LicenseForm;
